// Feature Request Collection Service
import { createProjectCollectionService, orderBy, where, limit, increment } from '../firebase/firestore';
import type { FeatureRequest, FeatureStatus } from '@/types';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase/config';

const featureService = createProjectCollectionService<FeatureRequest>('features');

export const featuresService = {
  ...featureService,

  // Get public features (for voting portal)
  getPublic: async (orgId: string, projectId: string) => {
    return featureService.getByProject(orgId, projectId, [
      where('isPublic', '==', true),
      orderBy('voteCount', 'desc'),
    ]);
  },

  // Get features by status
  getByStatus: async (orgId: string, projectId: string, status: FeatureStatus) => {
    return featureService.getByProject(orgId, projectId, [
      where('status', '==', status),
      orderBy('voteCount', 'desc'),
    ]);
  },

  // Get roadmap items (planned + in_progress)
  getRoadmap: async (orgId: string, projectId: string) => {
    return featureService.getByProject(orgId, projectId, [
      where('status', 'in', ['planned', 'in_progress']),
      where('isPublic', '==', true),
      orderBy('voteCount', 'desc'),
    ]);
  },

  // Get completed features
  getCompleted: async (orgId: string, projectId: string) => {
    return featureService.getByProject(orgId, projectId, [
      where('status', '==', 'released'),
      where('isPublic', '==', true),
      orderBy('updatedAt', 'desc'),
    ]);
  },

  // Get top voted features
  getTopVoted: async (orgId: string, projectId: string, count = 10) => {
    return featureService.getByProject(orgId, projectId, [
      where('isPublic', '==', true),
      orderBy('voteCount', 'desc'),
      limit(count),
    ]);
  },

  // Vote for a feature
  vote: async (featureId: string, voterId: string) => {
    const docRef = doc(db, 'features', featureId);
    await updateDoc(docRef, {
      voteCount: increment(1),
      voterIds: arrayUnion(voterId),
    });
  },

  // Remove vote
  unvote: async (featureId: string, voterId: string) => {
    const docRef = doc(db, 'features', featureId);
    await updateDoc(docRef, {
      voteCount: increment(-1),
      voterIds: arrayRemove(voterId),
    });
  },

  // Check if user has voted
  hasVoted: (feature: FeatureRequest, voterId: string): boolean => {
    return feature.voterIds?.includes(voterId) ?? false;
  },

  // Update status
  updateStatus: async (featureId: string, status: FeatureStatus) => {
    return featureService.update(featureId, { status });
  },

  // Make public/private
  setPublic: async (featureId: string, isPublic: boolean) => {
    return featureService.update(featureId, { isPublic });
  },

  // Get features submitted by email
  getBySubmitter: async (orgId: string, projectId: string, email: string) => {
    return featureService.getByProject(orgId, projectId, [
      where('submitterEmail', '==', email),
      orderBy('createdAt', 'desc'),
    ]);
  },
};
